<template lang="pug">
#add-sector-modal
	.modal-container(:class='{ active : state === "add-sector" }' @click.self='cancelSectorUpload')
		.modal-wrapper(:class='modalStep')
			.progress-indicators
				.progress-bar(v-bind:style='{ transform : `scale(${uploadProgress}, 1)` }')
				.loading-circles
					span
					span
					span
				.check.active
				.failure
					.failed
					.text Failed to Upload
			p.spacey {{ isEditing ? 'Edit' : 'Create a New' }} Sector
			input.name-input.spacey(v-model="newSectorName" type="text" placeholder="Sector Name")

			.row
				.file-input
					p.label {{ isEditing ? 'Edit' : 'Upload' }} PDF
					file-upload(
						v-show='isAdmin'
						@upload='uploadPDF'
						:allowed-ext='["pdf"]'
						:emit-immediately='true'
						:reset='state == "browse"'
						:id='"cte-upload"'
						:preview='pdfPreviewURL'
					)
				.file-input
					p.label {{ isEditing ? 'Edit' : 'Upload' }} Logo
					file-upload(
						v-show='isAdmin'
						@upload='uploadIcon'
						:allowed-ext='["png", "jpg", "jpeg"]'
						:emit-immediately='true'
						:reset='state == "browse"'
						:preview='newSector.iconUrl'
					)
			.buttons
				.button.no-bg(@click='cancelSectorUpload') cancel
				.button(@click='() => {  isEditing  ?  editSector()  :  saveSector()  }') save
			.error-message Please select a sector
</template>

<script>
import FileUpload from '@/components/FileUpload';
import { firebase, Ref, encodeKey } from '@/lib/db';

export default {
  name       : 'add-sector-modal',
  components : {
    FileUpload,
  },

  data : () => ( {
    newSectorName  : '',
    file           : null,
    icon           : null,
    uploadProgress : 0,
  } ),

  computed : {
    isAdmin() {
      return this.$store.state.isAdmin;
    },
    state() {
      return this.$store.state.modalState;
    },
    modalStep() {
      return this.$store.state.modalStep;
    },
    isEditing() {
      return this.$store.state.isEditing;
    },
    newSector() {
      return this.$store.state.newSector;
    },
    setSchool() {
      return this.$store.getters.setSchool;
    },
    setSchoolObj() {
      return this.$store.getters.getSchoolByID( this.setSchool );
    },
    pdfPreviewURL() {
      if ( this.setSchoolObj && this.setSchoolObj.industrySectors && this.setSchoolObj.industrySectors[this.newSector.id] ) {
        return this.setSchoolObj.industrySectors[this.newSector.id].pdfURL;
      }
      return this.newSector.pdfURL;
    }
  },

  watch : {
    // This component is always mounted, so need to watch for newSector changes to indicate open/closing
    newSector : {
      immediate : true,
      handler( sector ) {
        this.newSectorName = sector ? sector.name : '';
      }
    }
  },

  methods : {
    cancelSectorUpload() {
      this.$store.dispatch( 'closeSectorUpload' );
      this.file = null;
      this.icon = null;
    },

    uploadPDF( file ) {
      this.file = file;
    },

    uploadIcon( icon ) {
      this.icon = icon;
    },

    /**
	* Creates a new entry under industrySectors. If the schools filter has a selected value other than All,
	* add that sector (with the pdfURL, if given) to the selected school; otherwise just adds pdfURL to the
	* industry sector object.
	*/
    async saveSector() {
      this.$store.dispatch( 'updateStore', ['modalStep', 'open'] );
      if ( !this.newSectorName ) {
        this.$store.dispatch( 'updateStore', ['modalStep', 'error'] );
        return;
      }

      const uploadJobAsset = ( asset, path ) => {
        const upload = firebase.storage()
          .ref( path )
          .put( asset );
        upload.on( 'state_changed', ( snapshot ) => {
          this.uploadProgress = ( snapshot.bytesTransferred / snapshot.totalBytes );
        } );
        return upload.then( snapshot => snapshot.ref.getDownloadURL() );
      };

      const school = this.$store.getters.getSchoolByID( this.setSchool );
      const newSectorID = encodeKey( this.newSectorName );

      // How should we approach creating sectors??
      // For now, check the schools filter for if we need to update a school's
      // industrySectors list. Othwerise, just upload to "All" or w/e
      let pdfURL = null;
      let iconURL = null;
      if ( this.setSchoolObj ) {
        iconURL = ( await Ref.child( `industrySectors/${newSectorID}/iconURL` ).once( 'value' ) ).val();
      }
      if ( this.file ) {
        // If a school filter has been set, then add school name to the pdf link (otherwise would just override
        if ( this.setSchoolObj ) {
          pdfURL = await uploadJobAsset( this.file, `industrySectors/${this.setSchoolObj.id}/${newSectorID}.pdf` );
        }
        else {
          pdfURL = await uploadJobAsset( this.file, `industrySectors/${newSectorID}.pdf` );
        }
      }
      if ( this.icon ) {
        iconURL = await uploadJobAsset( this.icon, `industrySectors/${newSectorID}` );
      }
      const newSector = {
        id      : newSectorID,
        name    : this.newSectorName,
        schools : this.setSchoolObj ? { [this.setSchoolObj.id] : true } : null,
        iconURL
      };


      // If school filter is set, then add the school to the sector's schools list
      // Otherwise, put pdfURL on sector itself
      if ( this.setSchoolObj ) {
        newSector.schools = { [school.id] : true };
      }
      else {
        newSector.pdfURL = pdfURL;
      }

      Ref
        .child( `industrySectors/${newSectorID}` )
        .update( newSector )
        .then( () => {
          this.$store.dispatch( 'updateStore', ['modalStep', 'finished'] );
          window.setTimeout( () => {
            this.cancelSectorUpload();
          }, 2000 );
        } )
        .catch( () => {
          this.$store.$dispatch( 'updateStore', ['modalStep', 'failed'] );
        } );

      if ( this.setSchoolObj ) {
        Ref
          .child( `schools/${this.setSchoolObj.id}/industrySectors` )
          .update( {
            [newSectorID] : {
              provides : true,
              pdfURL,
            }
          } )
          .then( () => {
            this.$store.dispatch( 'updateStore', ['modalStep', 'finished'] );
            window.setTimeout( () => {
              this.cancelSectorUpload();
            }, 2000 );
          } )
          .catch( () => {
            this.$store.$dispatch( 'updateStore', ['modalStep', 'failed'] );
          } );
      }
    },
    async editSector() {
      this.$store.dispatch( 'updateStore', ['modalStep', 'uploading'] );

      const sectorID = this.newSector.id;
      const uploadJobAsset = ( asset, path ) => {
        const upload = firebase.storage()
          .ref( path )
          .put( asset );

        upload.on( 'state_changed', ( snapshot ) => {
          this.uploadProgress = ( snapshot.bytesTransferred / snapshot.totalBytes );
        } );
        return upload.then( snapshot => snapshot.ref.getDownloadURL() );
      };

      const school = this.$store.getters.getSchoolByID( this.setSchool );

      let pdfURL = this.newSector.pdfURL || null;
      let iconURL = this.newSector.iconURL || null;
      if ( this.file ) {
        // If a school filter has been set, then add school name to the pdf link (otherwise would just override
        // any other pdf uploaded.)
        if ( this.setSchoolObj ) {
          pdfURL = await uploadJobAsset( this.file, `industrySectors/${this.setSchoolObj.id}/${sectorID}.pdf` );
        }
        else {
          pdfURL = await uploadJobAsset( this.file, `industrySectors/${sectorID}.pdf` );
        }
      }

      if ( this.icon ) {
        iconURL = await uploadJobAsset( this.icon, `industrySectors/${sectorID}` );
      }

      const editedSector = ( () => {
        const obj = { name : this.newSectorName, iconURL };
        if ( !this.setSchoolObj ) obj.pdfURL = pdfURL;
        return obj;
      } )();

      // If school filter is set, then check if pdf should be updated
      if ( this.setSchoolObj && pdfURL ) {
        Ref
          .child( `schools/${school.id}/industrySectors/${sectorID}` )
          .update( { pdfURL } )
          .then( () => {
            this.$store.dispatch( 'updateStore', ['modalStep', 'finished'] );
            window.setTimeout( () => {
              this.cancelSectorUpload();
            }, 2000 );
          } )
          .catch( () => {
            this.$store.dispatch( 'updateStore', ['modalStep', 'failed'] );
          } );
      }

      Ref
        .child( `industrySectors/${sectorID}` )
        .update( editedSector )
        .then( () => {
          this.$store.dispatch( 'updateStore', ['modalStep', 'finished'] );
          window.setTimeout( () => {
            this.cancelSectorUpload();
          }, 2000 );
        } )
        .catch( () => {
          this.$store.dispatch( 'updateStore', ['modalStep', 'failed'] );
        } );
    },
  }
};
</script>

<style lang="scss">
#add-sector-modal {
	.modal-container {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(255,255,255,0.5);
		opacity: 0;
		transition: opacity 0.4s ease;
		pointer-events: none;
		z-index: 10;
		display: flex;
		align-items: center;
		justify-content: center;
		will-change: opacity;

		&.active {
			opacity: 1;
			pointer-events: all;

			.modal-wrapper {
				transform: scale( 1, 1 );

				.buttons .button {
					pointer-events: all;
				}
			}
		}

		.modal-wrapper {
			max-width: 700px;
			max-height: 90vh;
			transform: scale(0.8,0.8);
			transition: transform 0.4s ease;
			padding: 25px;
			border-radius: 10px;
			box-shadow: $floatingObject;
			background: white;
			position: relative;
			width: 80%;
			display: flex;
			flex-direction: column;
			overflow: hidden scroll;
			will-change: transition;

			&.error {

				.buttons {
					transform: translate3d(0, 0px, 0 );
				}

				.error-message {
					opacity: 1;
				}
			}

			&.uploading,
			&.finished,
			&.failed {

				.progress-indicators {
					opacity: 1;
				}
			}

			&.finished {

				.progress-indicators {

					.loading-circles {
						opacity: 0;
						transform: translate3d(-50%,calc(-50% - 30px),0);
					}

					.check {
						opacity: 1 !important;
						transform: translate3d(-50%,-50%,0) scale(3,3);
					}
				}
			}

			&.failed {

				.progress-indicators {

					.loading-circles {
						opacity: 0;
					}

					.failure {
						opacity: 1;
						transform: translate3d(-50%,-50%,0) scale(3,3);
					}
				}
			}

			.progress-indicators {
				position: absolute;
				top: 0;
				left: 0;
				right: 0;
				bottom: 0;
				background: white;
				pointer-events: none;
				opacity: 0;
				z-index: 100 !important;

				.loading-circles,
				.check,
				.failure {
					position: absolute;
					top: 50%;
					left: 50%;
					transform: translate3d(-50%,-50%, 0);
					transition: transform 0.5s ease, opacity 0.5s ease;
				}

				.check {
					opacity: 0;
					transform: translate3d(-50%,calc(-50% + 50px), 0) scale( 3, 3 );
				}

				.failure {
					transform: translate3d(-50%,calc(-50% + 50px), 0) scale(1,1);
					opacity: 0;
					display: flex;
					flex-direction: column;
					align-items: center;

					.failed {
						height: 30px;
						width: 30px;
						border-radius: 50%;
						background: $cancel;
						position: relative;

						&::before,
						&::after {
							content: ' ';
							position: absolute;
							top: 50%;
							left: 50%;
							height: 20px;
							width: 4px;
							border-radius: 2px;
							background: white;
						}

						&::before {
							transform: translate(-50%,-50%) rotate(45deg);
						}

						&::after {
							transform: translate(-50%,-50%) rotate(-45deg);
						}
					}

					.text {
						margin-top: 10px;
						font-size: 6px;
						color: $cancel;
					}
				}

				.progress-bar {
					position: absolute;
					height: 100%;
					width: 100%;
					top: 0;
					left: 0;
					background: $secondary;
					transition: transform 0.2s ease;
				}
			}

			.spacey {
				padding-bottom: 10px;
				border-bottom: 1px solid $lightGrey;
			}

			.sector-dropdown {
				z-index: 11!important;
				width: 100%;
				padding-top: 15px;

				.select-display {
					z-index: 2;

					p {
						width: 100%;

						&.minimal-label {
							left: 0;
						}
					}
				}
			}

			> p {
				pointer-events: none;
			}

			p {
				text-align: center;
			}

			.sector-name {
				margin-top: 10px;
			}

			.name-input {
				margin-top: 10px;
				font-size: 18px;
				text-align: center;
				&::placeholder {
					text-transform: uppercase;
					letter-spacing: 5px;
				}
			}

			.row {
				align-items: center;
				margin: 10px 0;

				.file-input {
					display: flex;
					flex-direction: column;
					align-items: center;
					flex: 1 1 0;

					p.label {
						color: $primary;
						font-size: 18px;
						font-weight: 600;
					}

					.file-upload {

						&.error {

							.file {
								display: none;
							}

							label {
								color: $cancel !important;
								border: 2px solid $cancel !important;
							}
						}

						.button {
							display: none !important;
						}

						.drop-zone {
							padding: 20px;
							display: flex;
							align-items: center;
							justify-content: center;
							flex-direction: row-reverse;
							flex-wrap: wrap;

							.upload-button {
								padding: 15px;
								border-radius: 30px;
								border: 2px solid $primary;
								text-transform: uppercase;
								background: transparent;
							}

							.file {
								overflow: hidden;

								p {
									overflow: hidden;
									text-overflow: ellipsis;
									font-size: 14px;
								}

							}

							.upload-button,
							.no-file p,
							.file,
							.error-msg p {
								color: $primary;
								font-size: 14px;
								letter-spacing: 5px;
								text-transform: uppercase;
							}

							.error-msg p {
								color: $cancel !important;
							}

							.file,
							.error-msg {
								margin: 0;
								margin-left: 15px;
							}

							.no-file {

								p {
									margin-left: 15px;

									&::before {
										content: 'or ';
									}

									&:nth-child(2) {
										display: none;
									}
								}
							}
						}

						p {
							color: $primary;

							&.error {
								color: $cancel;
							}
						}
					}
				}
			}

			> *:not(.file-upload) {
				z-index: 10;
			}

			.buttons {
				display: flex;
				align-items: flex-end;
				justify-content: center;
				pointer-events: none;
				transform: translate3d(0,20px,0);
				transition: transform 0.2s ease;

				.button {
					border-radius: 30px;
					margin: 0 15px;
					text-transform: uppercase;
					letter-spacing: 3px;
					font-size: 12px;
					padding: 15px;

					&.no-bg {
						background: transparent;
						color: $grey;
					}
				}
			}

			.error-message {
				color: $cancel;
				opacity: 0;
				transition: opacity 0.2s ease;
				text-align: center;
				pointer-events: none;
				margin-top: 10px;
			}
		}
	}
}
</style>
